import { FC } from 'react';
import { TutorialSection } from '@/types/tutorial';

interface CourseListProps {
    sections: TutorialSection[];
    onStartCourse: (sectionId: string) => void;
    setCategoryRef: (category: string, element: HTMLDivElement | null) => void;
}

// Helper to get cover image from section (handles both camelCase and snake_case)
const getCoverImage = (section: TutorialSection): string | undefined => {
    return section.coverImage || section.cover_image;
};

// Helper to get lesson count (handles both camelCase and snake_case)
const getLessonCount = (section: TutorialSection): number => {
    return section.lessonCount || section.lessons_count || section.lessons?.length || 0;
};

const CourseList: FC<CourseListProps> = ({ sections, onStartCourse, setCategoryRef }) => {
    // Group sections by category
    const sectionsByCategory = sections.reduce((acc, section) => {
        const category = section.category || 'Sales';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(section);
        return acc;
    }, {} as Record<string, TutorialSection[]>);

    const categories = Object.keys(sectionsByCategory);

    return (
        <div className='flex-1 min-w-0'>
            <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-gray-900">University</h2>
            </div>

            <div className='space-y-12'>
                {categories.map((category) => {
                    const categorySections = sectionsByCategory[category];
                    
                    return (
                        <div key={category} ref={(el) => setCategoryRef(category, el)}>
                            {/* Category Header */}
                            <h3 className="text-xl font-semibold text-gray-700 mb-6 pb-2 border-b border-gray-200">
                                {category}
                            </h3>
                            
                            {/* Courses in this category */}
                            <div className='space-y-8'>
                                {categorySections.map((section) => {
                                    const coverImage = getCoverImage(section);
                                    // Use actual lessons array length if available, otherwise fallback to lessonCount
                                    const actualLessonCount = section.lessons?.length || getLessonCount(section);
                                    const lessonsToShow = section.lessons?.slice(0, 3) || [];

                                    return (
                                        <div key={section.id} className="relative rounded-[32px] bg-white p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100/50">
                                            <div className="flex flex-col lg:flex-row gap-12 lg:items-stretch">
                                                {/* Content Column */}
                                                <div className="flex-1 flex flex-col pt-2">
                                                    <h3 className="text-4xl font-bold text-gray-900 mb-4 tracking-tight">
                                                        {section.title}
                                                    </h3>
                                                    <p className="text-slate-500 mb-10 text-xl leading-relaxed">
                                                        {section.description}
                                                    </p>

                                                    {/* Lesson List Preview */}
                                                    {lessonsToShow.length > 0 && (
                                                        <div className="flex-1 mb-10">
                                                            <div className="flex flex-col">
                                                                {lessonsToShow.map((lesson, idx) => (
                                                                    <div key={lesson.id || idx} className="flex items-center gap-4 py-5 border-b border-gray-100 last:border-0">
                                                                        <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
                                                                            <svg className="w-3.5 h-3.5 text-gray-400 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                                                                <path d="M8 5v14l11-7z" />
                                                                            </svg>
                                                                        </div>
                                                                        <span className="text-[17px] font-medium text-gray-700">{lesson.title}</span>
                                                                    </div>
                                                                ))}
                                                                {actualLessonCount > 3 && (
                                                                    <div className="mt-5 text-gray-400 font-medium text-[17px]">
                                                                        +{actualLessonCount - 3} more lessons
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="mt-auto">
                                                        <button
                                                            onClick={() => onStartCourse(section.id)}
                                                            className="inline-flex items-center justify-center px-8 py-3 border border-blue-600/30 text-blue-600 font-medium rounded-xl hover:bg-blue-50 transition-colors text-[15px]"
                                                        >
                                                            Start Course
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Visual/Image Column */}
                                                <div className="w-full lg:w-[480px] flex-shrink-0 rounded-[24px] overflow-hidden flex items-center justify-center min-h-[360px] bg-[#F9FAFB]">
                                                    {coverImage ? (
                                                        /* eslint-disable-next-line @next/next/no-img-element */
                                                        <img
                                                            src={coverImage}
                                                            alt={`${section.title} Cover`}
                                                            className="w-full h-auto object-contain max-h-[400px]"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-300 min-h-[300px]">
                                                            <span className="text-sm">Course Preview</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}

                {sections.length === 0 && (
                    <div className="text-center py-12 text-gray-500 bg-white rounded-2xl border border-gray-100">
                        No courses available.
                    </div>
                )}
            </div>
        </div>
    );
};

export default CourseList;
